import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useApp, formatINR, optimizedImage, imageSrcSet, handleImgError } from "../store";
import useDocumentMeta, { injectJsonLd } from "../hooks/useDocumentMeta";
import { ArrowLeft, Plus, Minus, FileText, ExternalLink, ShieldCheck, Beaker, FileCheck2, X, Download } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const { API, addToCart } = useApp();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [lots, setLots] = useState([]);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [unlockedDocs, setUnlockedDocs] = useState(null); // null until user submits inquiry

  useDocumentMeta({
    title: product ? (product.seo_title || product.name) : "Product",
    description: product
      ? (product.seo_description ||
          `${product.aroma_profile || ''} ${product.origin ? `Origin: ${product.origin}.` : ''} ${product.extraction_method ? `Extraction: ${product.extraction_method}.` : ''} Buy pure ${product.name} from House of Absolutes KA, Kannauj.`)
      : null,
    image: product?.image_url,
    type: "product",
    keywords: product?.keywords || [],
  });

  useEffect(() => {
    if (product) {
      injectJsonLd("ld-product", {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description: product.aroma_profile,
        image: product.image_url,
        brand: { "@type": "Brand", name: "House of Absolutes KA" },
        category: product.category,
        sku: product.id,
        offers: (product.variants || []).map(v => ({
          "@type": "Offer",
          name: `${product.name} — ${v.size}`,
          price: v.price,
          priceCurrency: "INR",
          availability: "https://schema.org/InStock",
          url: window.location.href,
        })),
      });
      // Breadcrumb schema
      injectJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
          { "@type": "ListItem", position: 2, name: "Catalog", item: `${window.location.origin}/catalog` },
          { "@type": "ListItem", position: 3, name: product.category, item: `${window.location.origin}/catalog?category=${encodeURIComponent(product.category)}` },
          { "@type": "ListItem", position: 4, name: product.name, item: window.location.href },
        ],
      });
      // FAQPage schema
      if (product.faqs && product.faqs.length > 0) {
        injectJsonLd("ld-faq", {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: product.faqs.map(f => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        });
      }
    }
    return () => {
      document.getElementById("ld-product")?.remove();
      document.getElementById("ld-breadcrumb")?.remove();
      document.getElementById("ld-faq")?.remove();
    };
  }, [product]);

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(r => {
      setProduct(r.data);
      setVariant(r.data.variants?.[0]);
    });
    axios.get(`${API}/products/${id}/lots`).then(r => setLots(r.data));
  }, [API, id]);

  if (!product) {
    return <div className="pt-32 px-6 md:px-12 max-w-[1500px] mx-auto text-[#8A8D8A] font-serif-display text-2xl italic">Loading…</div>;
  }

  const addToCartHandler = () => {
    addToCart(product, variant, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-[1500px] mx-auto" data-testid="product-detail-page">
      <Link to="/catalog" className="overline text-xs text-[#8A8D8A] hover:text-copper flex items-center gap-2 mb-10" data-testid="back-to-catalog">
        <ArrowLeft size={14} /> Back to catalog
      </Link>
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] bg-clay overflow-hidden">
            <img
              src={optimizedImage(product.image_url, 1200, 78)}
              srcSet={imageSrcSet(product.image_url, [500, 800, 1200, 1600], 78)}
              sizes="(max-width: 1024px) 100vw, 60vw"
              alt={product.seo_title || `Buy ${product.name}${product.botanical_name ? ` (${product.botanical_name})` : ''} online — pure natural oil from House of Absolutes KA, Kannauj`}
              loading="eager"
              fetchpriority="high"
              decoding="async"
              width="1200"
              height="1500"
              onError={handleImgError(product)}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
          <div className="overline text-copper mb-3">{product.category}</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none mb-3">{product.name}</h1>
          <p className="text-sm text-[#8A8D8A] italic mb-3">{product.botanical_name}</p>
          {product.keywords && product.keywords.length > 0 && (
            <p className="overline text-[10px] text-[#5C5E5C] mb-8" data-testid="product-keyword-line">
              Also known as: {product.keywords.slice(0, 3).join(" · ")}
            </p>
          )}

          <p className="text-base leading-relaxed text-[#5C5E5C] mb-10">{product.aroma_profile}</p>

          {/* Specs */}
          <dl className="border-t border-[#E5E1D8] divide-y divide-[#E5E1D8] mb-10">
            <SpecRow label="Origin" value={product.origin} />
            <SpecRow label="Extraction" value={product.extraction_method} />
            <SpecRow label="Plant Part" value={product.plant_part} />
            <SpecRow label="CAS No." value={product.cas_no || "—"} />
            {product.note && <SpecRow label="House Note" value={product.note} />}
          </dl>

          {/* Variant selector */}
          <div className="mb-8">
            <div className="overline text-xs mb-4">Pack Size</div>
            <div className="grid grid-cols-4 gap-2">
              {product.variants.map(v => (
                <button
                  key={v.size}
                  onClick={() => setVariant(v)}
                  className={`py-3 text-xs uppercase tracking-widest border transition-all ${variant?.size === v.size ? "border-[#1E1F1E] bg-[#1E1F1E] text-[#FAF9F6]" : "border-[#E5E1D8] hover:border-[#1E1F1E]"}`}
                  data-testid={`variant-${v.size}`}
                >
                  {v.size}
                </button>
              ))}
            </div>
            {variant && (
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-serif-display text-3xl">{formatINR(variant.price)}</span>
                <span className="overline text-[10px] text-[#8A8D8A]">incl. all taxes</span>
              </div>
            )}
            {variant?.size === "1kg+" && (
              <p className="text-xs text-copper mt-3">Bulk pricing — <Link to="/inquiry" className="underline">request a custom quote →</Link></p>
            )}
          </div>

          {/* Qty + add to cart */}
          <div className="flex items-stretch gap-3 mb-4">
            <div className="flex items-center border border-[#E5E1D8]">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="px-3 hover:bg-[#F3EFE9]" data-testid="qty-minus"><Minus size={14} /></button>
              <span className="px-4 text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(q => q + 1)} className="px-3 hover:bg-[#F3EFE9]" data-testid="qty-plus"><Plus size={14} /></button>
            </div>
            <button onClick={addToCartHandler} className="btn-primary flex-1 justify-center" data-testid="add-to-cart">
              {added ? "Added to cart ✓" : "Add to cart"}
            </button>
          </div>
          <button onClick={() => { addToCartHandler(); navigate("/cart"); }} className="btn-ghost w-full" data-testid="buy-now">Buy now</button>

          <div className="mt-10 pt-8 border-t border-[#E5E1D8] text-xs text-[#8A8D8A] leading-relaxed">
            <p>• COA &amp; origin documents shipped on request<br/>• 100% pure — no carriers, no diluents<br/>• Worldwide shipping · CITES permits where applicable</p>
          </div>
        </div>
      </div>

      {/* About + Usage section */}
      {(product.long_description || product.usage) && (
        <section className="mt-32 max-w-[1500px]" data-testid="product-about-section">
          <div className="border-t border-[#E5E1D8] pt-16 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="overline text-copper mb-3">About this material</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mb-8 leading-tight">{product.name}<span className="italic text-[#8A8D8A]">.</span></h2>
              {product.long_description && (
                <p className="text-base md:text-lg text-[#3C3D3C] leading-relaxed whitespace-pre-wrap" data-testid="product-long-description">
                  {product.long_description}
                </p>
              )}
            </div>
            {product.usage && (
              <aside className="lg:col-span-5 lg:pl-12 lg:border-l border-[#E5E1D8]">
                <div className="overline text-copper mb-4">Usage & Dosage</div>
                <p className="text-sm text-[#5C5E5C] leading-relaxed whitespace-pre-wrap" data-testid="product-usage">{product.usage}</p>
              </aside>
            )}
          </div>
        </section>
      )}

      {/* FAQs section */}
      {product.faqs && product.faqs.length > 0 && (
        <section className="mt-32 max-w-[1500px]" data-testid="product-faq-section">
          <div className="border-t border-[#E5E1D8] pt-16">
            <div className="overline text-copper mb-3">Common questions</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mb-10">FAQs<span className="italic text-[#8A8D8A]">.</span></h2>
            <div className="max-w-3xl divide-y divide-[#E5E1D8] border-y border-[#E5E1D8]">
              {product.faqs.map((f, i) => <Faq key={i} q={f.q} a={f.a} idx={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Certificates: GCMS + COA — gated behind inquiry */}
      {(product.documents && product.documents.length > 0) && (
        <section className="mt-32 max-w-[1500px]" data-testid="certificates-section">
          <div className="border-t border-[#E5E1D8] pt-16 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="overline text-copper mb-3">Certificates &amp; Lab Reports</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-6">
                {product.name}<span className="italic text-[#8A8D8A]"> — verified quality</span>
              </h2>
              <p className="text-base text-[#5C5E5C] leading-relaxed mb-6">
                Every batch of {product.name} is analyzed by an independent laboratory. Share your details
                below and we&rsquo;ll instantly grant you access to the <b>Certificate of Analysis (COA)</b> and
                <b> GC/MS report</b> for this material — {product.documents.length} document{product.documents.length === 1 ? "" : "s"} in total.
              </p>
              <div className="flex flex-wrap gap-4 text-xs text-[#5C5E5C]">
                <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-copper" strokeWidth={1.5} />100% pure — verified</div>
                <div className="flex items-center gap-2"><Beaker size={16} className="text-copper" strokeWidth={1.5} />GC/MS chromatography</div>
                <div className="flex items-center gap-2"><FileCheck2 size={16} className="text-copper" strokeWidth={1.5} />Batch traceable</div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="bg-sand p-8 md:p-10">
                <div className="grid grid-cols-2 gap-6 mb-8">
                  <ReportStat count={(product.documents || []).filter(d => d.type === "coa").length} label="COA reports" />
                  <ReportStat count={(product.documents || []).filter(d => d.type === "gcms").length} label="GC/MS reports" />
                </div>

                {unlockedDocs ? (
                  <div data-testid="unlocked-documents-list">
                    <div className="overline text-xs text-[#3C6348] mb-4">✓ Access granted — download below</div>
                    <div className="space-y-2">
                      {unlockedDocs.map(d => (
                        <a key={d.id} href={d.url} target="_blank" rel="noreferrer"
                           className="flex items-center justify-between gap-3 bg-white/70 hover:bg-white px-4 py-3 transition-colors"
                           data-testid={`unlocked-doc-${d.id}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            {d.type === "gcms" ? <Beaker size={16} className="text-copper shrink-0" strokeWidth={1.5} />
                                               : <FileCheck2 size={16} className="text-copper shrink-0" strokeWidth={1.5} />}
                            <div className="min-w-0">
                              <div className="text-xs uppercase tracking-widest text-[#8A8D8A]">{d.type === "gcms" ? "GC/MS Report" : "Certificate of Analysis"}</div>
                              <div className="text-sm truncate">{d.label || d.filename}</div>
                            </div>
                          </div>
                          <Download size={16} className="text-copper shrink-0" strokeWidth={1.5} />
                        </a>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#8A8D8A] mt-4">Files open in a new tab. Our team may follow up shortly via email.</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-[#5C5E5C] mb-6">
                      Reports are shared with verified buyers &amp; formulators only. It only takes a moment.
                    </p>
                    <button
                      onClick={() => setDocModalOpen(true)}
                      className="btn-primary"
                      data-testid="request-documents-btn"
                    >
                      Request COA &amp; GC/MS reports
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Lot Reports section */}
      {lots.length > 0 && (
        <section className="mt-32 max-w-[1500px]" data-testid="lots-section">
          <div className="border-t border-[#E5E1D8] pt-16">
            <div className="overline text-copper mb-3">Provenance &amp; Lab Reports</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mb-10">Available Lots</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-1">
              {lots.map(l => (
                <div key={l.id} className="bg-sand p-6" data-testid={`lot-card-${l.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-mono text-sm">{l.lot_number}</div>
                      <div className="text-xs text-[#8A8D8A] mt-1">Distilled · {l.batch_date}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${l.status === "active" ? "bg-[#3C6348]/10 text-[#3C6348]" : "bg-[#EBE4D8] text-[#5C5E5C]"}`}>{l.status.replace("_"," ")}</span>
                  </div>
                  {l.notes && <p className="text-xs text-[#5C5E5C] mb-4">{l.notes}</p>}
                  <div className="flex flex-col gap-2 text-xs">
                    {l.coa_url ? (
                      <a href={l.coa_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-copper hover:underline" data-testid={`lot-coa-${l.id}`}>
                        <FileText size={14} strokeWidth={1.5} /> Certificate of Analysis <ExternalLink size={11} />
                      </a>
                    ) : <span className="text-[#8A8D8A]">COA on request</span>}
                    {l.gcms_url ? (
                      <a href={l.gcms_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-copper hover:underline" data-testid={`lot-gcms-${l.id}`}>
                        <FileText size={14} strokeWidth={1.5} /> GC/MS Report <ExternalLink size={11} />
                      </a>
                    ) : <span className="text-[#8A8D8A]">GC/MS on request</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {docModalOpen && (
        <DocumentRequestModal
          product={product}
          onClose={() => setDocModalOpen(false)}
          onUnlocked={(docs) => {
            setUnlockedDocs(docs);
            setDocModalOpen(false);
            // Scroll the unlocked section into view
            setTimeout(() => {
              document.querySelector('[data-testid="unlocked-documents-list"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);
          }}
        />
      )}
    </div>
  );
}

function ReportStat({ count, label }) {
  return (
    <div className="text-center">
      <div className="font-serif-display text-5xl text-copper leading-none">{count}</div>
      <div className="overline text-[10px] text-[#8A8D8A] mt-2">{label}</div>
    </div>
  );
}

function DocumentRequestModal({ product, onClose, onUnlocked }) {
  const { API } = useApp();
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/products/${product.id}/document-request`, form);
      onUnlocked(r.data.documents || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Could not submit. Please try again.";
      setError(typeof msg === "string" ? msg : "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4" data-testid="doc-request-modal">
      <div className="bg-[#FAF9F6] w-full max-w-lg p-8 md:p-10 relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2" data-testid="doc-request-close">
          <X size={20} />
        </button>
        <div className="overline text-copper mb-2">Request access</div>
        <h3 className="font-serif-display text-3xl mb-2">Lab reports for {product.name}</h3>
        <p className="text-sm text-[#5C5E5C] mb-6">
          Share your details and download the COA + GC/MS files immediately. We&rsquo;ll only reach out for
          bulk pricing or lot-specific queries.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="overline text-[10px] mb-1">Name *</div>
            <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="input-underline w-full" data-testid="doc-req-name" />
          </label>
          <label className="block">
            <div className="overline text-[10px] mb-1">Work email *</div>
            <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              className="input-underline w-full" data-testid="doc-req-email" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <div className="overline text-[10px] mb-1">Company</div>
              <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                className="input-underline w-full" data-testid="doc-req-company" />
            </label>
            <label className="block">
              <div className="overline text-[10px] mb-1">Phone</div>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                className="input-underline w-full" data-testid="doc-req-phone" />
            </label>
          </div>
          <label className="block">
            <div className="overline text-[10px] mb-1">Anything specific? (optional)</div>
            <textarea rows={2} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
              className="input-underline w-full resize-none" placeholder="e.g. bulk 5 kg quote, specific lot #, formulation notes…"
              data-testid="doc-req-message" />
          </label>
          {error && <div className="text-xs text-[#9E3C3C]" data-testid="doc-req-error">{error}</div>}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60"
            data-testid="doc-req-submit">
            {loading ? "Submitting…" : "Submit & download reports"}
          </button>
          <p className="text-[10px] text-[#8A8D8A] text-center">
            We keep your info private. No spam — just certification &amp; follow-up on your inquiry.
          </p>
        </form>
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  return (
    <div className="flex py-4">
      <dt className="w-1/3 overline text-[10px] text-[#8A8D8A]">{label}</dt>
      <dd className="flex-1 text-sm">{value}</dd>
    </div>
  );
}

function Faq({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-5" data-testid={`product-faq-${idx}`}>
      <button onClick={() => setOpen(v => !v)} className="w-full flex justify-between items-start text-left gap-6" data-testid={`product-faq-toggle-${idx}`}>
        <span className="font-serif-display text-lg md:text-xl leading-snug pr-4">{q}</span>
        <span className="font-serif-display text-2xl text-copper shrink-0 leading-none mt-1">{open ? "–" : "+"}</span>
      </button>
      {open && (
        <p className="mt-4 text-sm md:text-base text-[#5C5E5C] leading-relaxed" data-testid={`product-faq-answer-${idx}`}>{a}</p>
      )}
    </div>
  );
}
